import { timezone } from 'core/util/dateUtils';

export const MATCH_STATES = {
  MATCH: 'match',
  MISSING_DATA: 'missingData',
  SKIPPED: 'skipped'
};

// Used to determine data groupings for alert chart plot values.
export function parseBaselineUsed(key) {
  if (!key) {
    return { matchState: MATCH_STATES.MATCH, currentIsFallback: false, baselineIsFallback: false };
  }

  let matchState;
  if (key.endsWith('_SKIP')) {
    matchState = MATCH_STATES.SKIPPED;
  } else if (key.endsWith('_EXISTS')) {
    matchState = MATCH_STATES.MISSING_DATA;
  } else {
    matchState = MATCH_STATES.MATCH;
  }

  const hasFallbackSuffix = key.endsWith('_DEFAULT') || key.endsWith('_LOWEST') || key.endsWith('_HIGHEST');
  const currentIsFallback = hasFallbackSuffix && key.startsWith('ACT_CURRENT');
  const baselineIsFallback = hasFallbackSuffix && key.startsWith('ACT_BASELINE');

  return { matchState, currentIsFallback, baselineIsFallback };
}

export function getTimeRangeAroundAlertStart(midpoint, marginSeconds) {
  const now = timezone.momentFn();

  // Always anchor on midpoint (alert start time) — using endTime would make the window
  // as large as the alert duration, which can be days for long-running alerts.
  // endTime is only used as a visual reference in the chart (plotBand), not here.
  const start = timezone.momentFn(midpoint).subtract(marginSeconds, 'seconds');
  const end = timezone.momentFn(midpoint).add(marginSeconds, 'seconds');

  return {
    start: start.toISOString(),
    end: end.isAfter(now) ? now.toISOString() : end.toISOString()
  };
}

const ALARM_CLASS = 'alarm-event';

/**
 * Returns { minMs, maxMs } for chart x-axis bounds, capped at now.
 * Returns { minMs: undefined, maxMs: undefined } when lookbackSeconds is falsy.
 */
export function getChartTimeRange(startTime, lookbackSeconds) {
  if (!lookbackSeconds) {
    return { minMs: undefined, maxMs: undefined };
  }

  const { start, end } = getTimeRangeAroundAlertStart(startTime, lookbackSeconds);
  const maxMs = Math.min(Date.parse(end), Date.now());
  return { minMs: Date.parse(start), maxMs };
}

/**
 * Returns the Highcharts plotBand config for the "Alert Active" shaded zone,
 * or null if startMs is not finite. Pass endMs=undefined for active (ongoing) alerts.
 */
export function getAlertActiveBand(startMs, endMs) {
  const isActive = !Number.isFinite(endMs);
  const to = isActive ? Infinity : endMs;

  if (!Number.isFinite(startMs) || (!isActive && to < startMs)) {
    return null;
  }

  return {
    id: 'alert-active-band',
    className: `${ALARM_CLASS} no-outline`,
    from: startMs,
    to,
    label: {
      text: 'Active',
      align: isActive ? 'left' : 'center',
      x: isActive ? 8 : 0,
      className: ALARM_CLASS
    },
    zIndex: 2
  };
}

/**
 * Returns Highcharts plotLine configs for the alert start (and optionally end) boundaries.
 * Returns [] if startMs is not finite.
 */
export function getAlertBoundaryLines(startMs, endMs) {
  if (!Number.isFinite(startMs)) {
    return [];
  }

  const lines = [
    {
      id: 'alert-start-line',
      value: startMs,
      className: ALARM_CLASS,
      dashStyle: 'Solid',
      width: 2,
      zIndex: 3
    }
  ];

  if (Number.isFinite(endMs)) {
    lines.push({
      id: 'alert-end-line',
      value: endMs,
      className: ALARM_CLASS,
      dashStyle: 'Solid',
      width: 2,
      zIndex: 3
    });
  }

  return lines;
}
